import { ServiceLuppPaginatedData, ServiceLuppResponse } from './types';
import { createApi } from '../../utils.api';
import { PaginatedResponse } from '../../types.api';
import { LUPP_BASE_URL } from './constants.lupp';
import { getTokenSilently, getOrgId, getUserId } from '@lucid/auth';
import { getPortalStoreState } from '@lucid/portal-core';

const luppInstance = createApi<ServiceLuppResponse<unknown>>(LUPP_BASE_URL || '', {
  extractData: (response: ServiceLuppResponse<unknown>) => response.data,
  paginate: (response: ServiceLuppResponse<unknown>) => paginateLuppResponse(response),
  extractError: (data: unknown) => extractLuppError(data as ServiceLuppResponse<unknown>),
});

// Add interceptor to automatically inject required headers from user identity
luppInstance.instance.interceptors.request.use(async (config) => {
  const idToken = await getTokenSilently();

  // Skip header validation for the /me endpoint since it's needed to get user identity
  const isUserIdentityEndpoint = config.url?.includes('/partners/me');

  // Set Authorization header for all requests if token is available
  if (idToken) {
    config.headers.set('Authorization', `Bearer ${idToken}`);
  }

  if (isUserIdentityEndpoint) {
    // getUserId() reads from localStorage which may not be populated on the very first
    // request (Auth0 writes it asynchronously after the callback). Fall back to decoding
    // the `sub` claim directly from the JWT so X-Partner-Id is always present.
    const userId = getUserId() || (idToken ? JSON.parse(atob(idToken.split('.')[1])).sub : '');
    config.headers.set('X-Auth-Retry-Max', '0');
    if (userId) {
      config.headers.set('X-Partner-Id', userId);
    }
    const orgId = getOrgId();
    if (orgId) {
      config.headers.set('X-Org-Id', orgId);
    }
    return config;
  }

  // Get user identity from portal-core store (in-memory)
  const userIdentity = getPortalStoreState().userIdentity;
  const userId = getUserId();

  // Validate that we have both required identifiers before allowing API calls
  if (!userId || !userIdentity?.auth0OrganizationId) {
    throw new Error(
      'Authentication required: Missing user ID or organization ID. Please log in again.'
    );
  }

  // Validate that the Auth0 user ID matches the stored user identity for security
  if (userIdentity.auth0UserId !== userId) {
    throw new Error(
      'Authentication mismatch: User identity validation failed. Please log in again.'
    );
  }

  // Set headers with validated data
  config.headers.set('X-Partner-Id', userId);
  config.headers.set('X-Org-Id', userIdentity.auth0OrganizationId);

  return config;
});

export const LuppApi = luppInstance;

// FileHub API instance - uses same base URL as LUPP
const filehubInstance = createApi<ServiceLuppResponse<unknown>>(LUPP_BASE_URL || '', {
  extractData: (response: ServiceLuppResponse<unknown>) => response.data,
  extractError: (data: unknown) => extractLuppError(data as ServiceLuppResponse<unknown>),
});

// Add interceptor for FileHub with X-Tenant-Id header and Authorization
filehubInstance.instance.interceptors.request.use(async (config) => {
  const idToken = await getTokenSilently();

  // Set Authorization header if token is available
  if (idToken) {
    config.headers.set('Authorization', `Bearer ${idToken}`);
  }

  config.headers.set('X-Tenant-Id', 'lucid-partner-us');
  config.headers.set('X-Partner-Id', getUserId());
  const userIdentity = getPortalStoreState().userIdentity;
  if (userIdentity?.auth0OrganizationId) {
    config.headers.set('X-Org-Id', userIdentity.auth0OrganizationId);
  }
  return config;
});

export const FileHubApi = filehubInstance;

function extractLuppError(response: ServiceLuppResponse<unknown>): string | null {
  const errors = response?.errors?.length > 0 ? response.errors.join(', ') : null;
  const message = response?.message?.trim() || null;

  if (errors && message) return `${message}: ${errors}`;

  if (errors) return errors;
  if (message) return message;

  return null;
}

function paginateLuppResponse(response: ServiceLuppResponse<unknown>): PaginatedResponse<unknown> {
  return {
    items: response.data,
    pagination: response.pagination,
  };
}

export function transformPaginatedLupp<TInput, TOutput>(
  LuppPaginatedData: ServiceLuppPaginatedData<TInput>,
  transformer: (data: TInput) => TOutput
): ServiceLuppPaginatedData<TOutput> {
  return {
    items: transformer(LuppPaginatedData.items),
    pagination: LuppPaginatedData.pagination,
  };
}

export function paginateLuppRequest(page: number = 0, size: number = 10) {
  return `size=${size}&page=${page + 1}`;
}
